import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Chip,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';
import { DiagnosisReadDTO } from '../../generated/api-client';

export interface DiagnosisAdvancedFilterState {
  customer: string;
  ateco: string;
  energyUseCodes: string[];
}

export const EMPTY_ADVANCED_FILTER: DiagnosisAdvancedFilterState = {
  customer: '',
  ateco: '',
  energyUseCodes: [],
};

export interface EnergyUseOption {
  code: string;
  description?: string;
}

export interface DiagnosisAdvancedFilterProps {
  diagnoses: DiagnosisReadDTO[];
  customerNameByDiagnosisId: Record<string, string>;
  atecoByDiagnosisId: Record<string, string>;
  energyUsesByDiagnosisId: Record<string, EnergyUseOption[]>;
  value: DiagnosisAdvancedFilterState;
  onChange: (next: DiagnosisAdvancedFilterState) => void;
}

const normalize = (value: string) => value.trim().toLowerCase();

export function applyDiagnosisAdvancedFilter(
  diagnoses: DiagnosisReadDTO[],
  filter: DiagnosisAdvancedFilterState,
  customerNameByDiagnosisId: Record<string, string>,
  atecoByDiagnosisId: Record<string, string>,
  energyUsesByDiagnosisId: Record<string, EnergyUseOption[]>
): DiagnosisReadDTO[] {
  const customerQ = normalize(filter.customer);
  const atecoQ = normalize(filter.ateco);

  return diagnoses.filter(d => {
    const did = d.id ?? '';
    const customerName = customerNameByDiagnosisId[did] ?? '';
    const ateco = atecoByDiagnosisId[did] ?? '';

    if (customerQ && !normalize(customerName).includes(customerQ)) return false;
    if (atecoQ && !normalize(ateco).startsWith(atecoQ)) return false;

    if (filter.energyUseCodes.length > 0) {
      const uses = energyUsesByDiagnosisId[did] ?? [];
      const codes = new Set(uses.map(u => u.code));
      const hasMatch = filter.energyUseCodes.some(code => codes.has(code));
      if (!hasMatch) return false;
    }

    return true;
  });
}

export const DiagnosisAdvancedFilter: React.FC<DiagnosisAdvancedFilterProps> = ({
  diagnoses,
  customerNameByDiagnosisId,
  atecoByDiagnosisId,
  energyUsesByDiagnosisId,
  value,
  onChange,
}) => {
  const prerequisitesMet = Boolean(value.customer.trim() || value.ateco.trim());

  const candidateDiagnoses = React.useMemo(() => {
    if (!prerequisitesMet) return [];
    const customerQ = normalize(value.customer);
    const atecoQ = normalize(value.ateco);
    return diagnoses.filter(d => {
      const did = d.id ?? '';
      const customerName = customerNameByDiagnosisId[did] ?? '';
      const ateco = atecoByDiagnosisId[did] ?? '';
      if (customerQ && !normalize(customerName).includes(customerQ)) return false;
      if (atecoQ && !normalize(ateco).startsWith(atecoQ)) return false;
      return true;
    });
  }, [diagnoses, customerNameByDiagnosisId, atecoByDiagnosisId, value.customer, value.ateco, prerequisitesMet]);

  const energyUseOptions = React.useMemo(() => {
    const map = new Map<string, EnergyUseOption>();
    candidateDiagnoses.forEach(d => {
      const did = d.id ?? '';
      (energyUsesByDiagnosisId[did] ?? []).forEach(use => {
        if (use.code && !map.has(use.code)) {
          map.set(use.code, use);
        }
      });
    });
    return Array.from(map.values()).sort((a, b) => a.code.localeCompare(b.code));
  }, [candidateDiagnoses, energyUsesByDiagnosisId]);

  const activeFilterCount =
    (value.customer.trim() ? 1 : 0) +
    (value.ateco.trim() ? 1 : 0) +
    (value.energyUseCodes.length > 0 ? 1 : 0);

  const handleReset = () => onChange(EMPTY_ADVANCED_FILTER);

  return (
    <Accordion disableGutters variant="outlined" sx={{ borderRadius: 2, '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1} alignItems="center">
          <FilterListIcon fontSize="small" color="action" />
          <Typography variant="subtitle2">Ricerca avanzata</Typography>
          {activeFilterCount > 0 && (
            <Chip size="small" label={`${activeFilterCount} filtri attivi`} color="primary" />
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2}>
          <Typography variant="caption" color="text.secondary">
            Filtra per cliente e/o codice ATECO (match parziale). Dopo aver impostato almeno uno dei
            due, puoi restringere per usi energetici.
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label="Cliente"
              size="small"
              fullWidth
              value={value.customer}
              onChange={e =>
                onChange({
                  ...value,
                  customer: e.target.value,
                  energyUseCodes: [],
                })
              }
              placeholder="Es. Forge Monchieri"
            />
            <TextField
              label="Codice ATECO"
              size="small"
              fullWidth
              value={value.ateco}
              onChange={e =>
                onChange({
                  ...value,
                  ateco: e.target.value,
                  energyUseCodes: [],
                })
              }
              placeholder="Es. 25"
            />
          </Stack>
          <Box>
            <Autocomplete
              multiple
              size="small"
              disabled={!prerequisitesMet}
              options={energyUseOptions}
              getOptionLabel={option =>
                option.description ? `${option.code} — ${option.description}` : option.code
              }
              value={energyUseOptions.filter(o => value.energyUseCodes.includes(o.code))}
              onChange={(_, selected) =>
                onChange({
                  ...value,
                  energyUseCodes: selected.map(s => s.code),
                })
              }
              renderInput={params => (
                <TextField
                  {...params}
                  label="Usi energetici"
                  placeholder={
                    prerequisitesMet
                      ? 'Seleziona uno o più usi energetici'
                      : 'Imposta prima Cliente o ATECO'
                  }
                />
              )}
            />
          </Box>
          <Box>
            <Button size="small" onClick={handleReset} disabled={activeFilterCount === 0}>
              Azzera filtri
            </Button>
          </Box>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};
